// ─── YUVASETU · National School AI Hackathon 2026 ─────────────────────────────
// Single source of truth for every piece of event information on /hackathon and
// /hackathon/register. Components only read from here.
//
// Programme data below comes from the YUVASETU Execution Plan (30 Sep 2026).
// Rules that must survive future edits:
//   • Participation numbers are TARGETS, not results — always word them as
//     "Targeting 15,000 students", never "15,000 students participated".
//   • The finale venue is NOT locked. Say "New Delhi" only, never Vigyan Bhawan,
//     until the organising team confirms it.
//   • Do not publish internal operations (budgets, call targets, staffing).
//   • Anything still unconfirmed stays TBD — never replace a TBD with a guess.

export const TBD = "TBD";
export const SOON = "Coming soon";

export const hackathon = {
  eventName: "YUVASETU",
  title: "National School AI Hackathon 2026",
  tagline: "Young Minds. Real-World Solutions.",
  theme: "Tech for a Self-Reliant Bharat",
  pitch: "Find a real problem. Build a real solution.",
  // Who runs YUVASETU. A school or parent meeting the event for the first time
  // has no reason to trust it yet, so the page says plainly who is behind it.
  // Every claim below is one the main site already makes (see AboutUs and the
  // Shark Tank section) — kept word for word so the two can never disagree.
  organiser: {
    name: "CoGrad",
    url: "https://cograd.in/",
    blurb: "CoGrad has been building technology for Indian schools since 2019, from rural classrooms to city campuses.",
    sharkTank: {
      badge: "Funded on Shark Tank India · Season 4",
      dealNote: "₹1 crore raised from three sharks on national television.",
      pitchUrl: "https://youtu.be/jMkCnEDCJo8",
      pitchLabel: "Watch the pitch",
      photoAlt: "CoGrad's founders with the sharks on Shark Tank India after securing their deal",
    },
  },

  // ── Cancellation ────────────────────────────────────────────────────────────
  // YUVASETU 2026 was called off on 4 Nov 2026. While `active` is true the page
  // leads with this notice, every registration path reads "closed", and the
  // home-page band is withdrawn. Refunds are started by the team manually.
  cancellation: {
    active: true,
    badge: "Important update",
    title: "YUVASETU 2026 has been cancelled",
    short: "Hackathon Cancelled",
    body:
      "Due to unavoidable internal reasons, we have had to cancel YUVASETU — National School AI Hackathon 2026. We are truly sorry to every student, mentor teacher and school who registered and was looking forward to it.",
    refundTitle: "Your fee will be refunded within 24–48 hours",
    refundShort: "Every registration fee will be refunded within 24–48 hours.",
    refundPoints: [
      "The refund goes back to the same UPI, card or bank account you paid from.",
      "You do not need to apply or fill any form — it happens on its own.",
      "Once we send it, some banks take a few working days to show it in your account.",
    ],
    help: "Not received it, or have a question? Write to us with your Registration ID.",
    thanks: "Thank you for your trust and understanding.",
  },

  seo: {
    title: "YUVASETU — National School AI Hackathon 2026",
    description:
      "YUVASETU — National School AI Hackathon 2026 has been cancelled. Every registration fee will be refunded within 24–48 hours.",
    canonical: "https://cograd.in/hackathon",
    ogImage: "https://cograd.in/og-yuvasetu.jpg",
  },

  registration: {
    // 10 Oct 2026 00:00 IST → 2 Nov 2026 23:59 IST
    opensAt: "2026-10-10T00:00:00+05:30",
    opensLabel: "10 October 2026",
    opensShort: "10 Oct",
    closesAt: "2026-11-02T23:59:59+05:30",
    closesLabel: "2 November 2026",
    closesShort: "2 Nov",
    windowLabel: "10 Oct – 2 Nov 2026",
    // A team member pays less than a solo entrant, once per member. A mentor
    // teacher is never charged, so they never count towards the total.
    fee: { individual: 399, team: 299 },
    // Short label for chips and hero strips, where only one number fits.
    feeLabel: "From ₹299",
    feeUnit: "per student",
    // Both numbers, for the places that have room to say it properly.
    feeDetailLabel: "₹399 solo · ₹299 per member in a team",
    feeIndividualLabel: "₹399",
    feeTeamLabel: "₹299",
    team: { min: 2, max: 4 },
    methods: ["UPI", "Card", "Net Banking"],
    paymentNote: "Pay by UPI, card or net banking.",
    // What a student gets once the form is submitted
    receives: [
      "Registration ID",
      "Personal referral link",
      "Team code",
      "Confirmation email",
      "WhatsApp confirmation and channel invite",
    ],
    path: "/hackathon/register",
  },

  // Offers run alongside the standard fee — never instead of it.
  offers: [
    {
      id: "referral",
      title: "Refer friends",
      value: "₹100 back",
      note: "Refer 3 friends for ₹100 cashback or a Campus Ambassador certificate. Refer 10 and your team gets a guaranteed bootcamp seat.",
    },
    {
      id: "sponsored",
      title: "Sponsored seats",
      value: "Fee waiver",
      note: "Waiver codes are planned for low-income and government-school students.",
    },
  ],

  eligibility: {
    classes: "8–12",
    classList: ["8", "9", "10", "11", "12"],
    schools: "All schools, all boards",
    boards: ["CBSE", "ICSE", "State Boards", "Other recognised boards"],
    reach: "Government and private schools across India, urban and rural.",
    teamSize: "2–4 students",
    mentor: "1 mentor teacher",
    teamLabel: "2–4 students + 1 mentor teacher",
    soloNote: "You can also register on your own and form or join a team before the Idea Round.",
    category: "One open category for Classes 8–12. Every team is judged together.",
  },

  // Programme targets from the execution plan — NOT achieved results.
  targets: {
    note: "Targets for the 2026 edition, not results.",
    items: [
      { value: "15,000", label: "Students" },
      { value: "1,500", label: "Schools" },
      { value: "25+", label: "States & UTs" },
      { value: "40%+", label: "Girls' participation" },
    ],
  },

  // Quick facts strip under the hero
  facts: [
    { value: "8–12",            label: "Classes" },
    { value: "All boards",      label: "Schools" },
    { value: "2–4 + 1",         label: "Students + mentor" },
    { value: "6",               label: "Challenge tracks" },
    { value: "10 Oct – 2 Nov",  label: "Registration" },
    { value: "12 Jan 2027",     label: "Grand Finale" },
    { value: "From ₹299",       label: "Per student" },
  ],

  // Section tabs / side-rail stops (ids match the section ids on the page)
  tabs: [
    { id: "prizes",      label: "Prize Pool" },
    { id: "about",       label: "About" },
    { id: "participate", label: "How It Works" },
    { id: "tracks",      label: "Challenge Tracks" },
    { id: "judging",     label: "Judging" },
    { id: "legacy",      label: "Previous Programmes" },
    { id: "faq",         label: "FAQs" },
  ],

  nav: [
    { id: "about",     label: "About" },
    { id: "tracks",    label: "Tracks" },
    { id: "participate", label: "How it works" },
    { id: "rewards",   label: "Prizes" },
    { id: "faq",       label: "FAQ" },
  ],

  // Headings and short copy per section. `title` is [plain, highlighted].
  sections: {
    tracks: {
      title: ["Six Challenge", "Tracks."],
      sub: "Pick the one your problem belongs to. Every team competes in a single open category.",
    },
    about: {
      title: ["About", "YUVASETU."],
      body: "YUVASETU is a national school AI hackathon for young innovators in Classes 8 through 12. We believe the next generation of India's builders already exist — in classrooms, villages, and cities across the country. The challenge is simple: find a real problem around you, and build a technology-driven solution. Through YUVASETU, students gain hands-on experience in problem-solving, design thinking, coding, and AI — guided by mentors and industry experts. The hackathon runs across six problem domains: Education, Health, Environment & Cleanliness, Agriculture & Rural Life, Safety & Smart Communities, and Open Innovation. Every team competes in one open category, judged on the strength of their idea, innovation, feasibility, and ability to create real impact.",
      cta: "Whether you're coding your first project or building your tenth, whether you're from a government school or a private one, YUVASETU is your bridge from learning to building.",
    },
    prizes: {
      title: ["Prizes &", "Recognition."],
      sub: "One open category. All teams from Classes 8 to 12 are ranked together.",
      specialTitle: "Special awards · ₹10,000 each",
      recognitionNote:
        "Every finalist receives a medal and a certificate. Every finalist's mentor teacher is honoured on stage.",
    },
    judging: {
      title: ["Judging &", "Evaluation."],
      sub: "Every team is scored on the same criteria. The weightage changes from round to round.",
    },
    finale: {
      title: ["Grand Finale.", "New Delhi."],
      sub: "30 finalist teams pitch on National Youth Day, 12 January 2027.",
    },
    legacy: {
      chip: "Proof of Experience",
      title: ["Built Before.", "Building Again."],
      sub: "YUVASETU builds on a growing ecosystem of student innovation, AI and real-world problem solving.",
    },
    faq: {
      title: ["Frequently Asked", "Questions."],
      contactTitle: "Still have a question?",
      contactPending: "Contact details for the organising team are being finalised and will be published here soon.",
    },
    cta: {
      title: ["Registrations Open", "10 Oct – 2 Nov 2026."],
      sub: "One form per student. It takes about two minutes.",
      button: "Go to registration",
      secondary: "Read the FAQs",
    },
  },

  about: {
    headline: ["Find a real problem.", "Build a real solution."],
    body:
      "YUVASETU is a national school AI hackathon where students turn a problem they have seen themselves into a working technology-driven solution.",
    places: ["School", "Home", "Village", "City"],
    bridge: ["Problem", "Idea", "Build", "Prototype", "Impact"],
  },

  // The six official challenge tracks with problem statements and starter ideas.
  tracks: [
    {
      id: "education",
      n: "01",
      name: "Education",
      problem: "AI study buddy for local languages",
      description: "Students study in English but think in their mother tongue, and good explanations in regional languages are scarce.",
      idea: "An AI that explains a maths step or a science idea in the student's own language.",
      tags: ["Chatbot", "Translate", "Voice"],
    },
    {
      id: "health",
      n: "02",
      name: "Health",
      problem: "Early symptom helper for rural families",
      description: "Families delay care because the nearest doctor is far and they can't tell whether a symptom is serious.",
      idea: "An AI that asks simple questions, then says rest, visit a clinic or seek urgent help — never a diagnosis.",
      tags: ["Guided Q&A", "Chatbot", "First aid"],
    },
    {
      id: "environment",
      n: "03",
      name: "Environment & Cleanliness",
      problem: "AI waste sorter for the school",
      description: "Waste gets mixed because most people don't know what counts as wet, dry, recyclable or e-waste.",
      idea: "An AI that reads a photo of an item, names its bin, and tracks what the school recycles.",
      tags: ["Photo sort", "Bin finder", "Dashboard"],
    },
    {
      id: "agriculture",
      n: "04",
      name: "Agriculture & Rural Life",
      problem: "Crop doctor for farmers",
      description: "A farmer sees spots on a leaf but can't tell which disease it is or what to do about it.",
      idea: "An AI that names the disease from a leaf photo and suggests low-cost remedies in the local language.",
      tags: ["Leaf photo", "Advice app"],
    },
    {
      id: "safety",
      n: "05",
      name: "Safety & Smart Communities",
      problem: "AI helper for a classmate with a disability",
      description: "Students with low vision, hearing loss or a learning difficulty can't use the same materials as everyone else.",
      idea: "An AI that reads text aloud from a photo, captions speech live, or describes an image for a blind student.",
      tags: ["Read aloud", "Captions", "Describe"],
    },
    {
      id: "open",
      n: "06",
      name: "Open Innovation",
      problem: "AI to catch fake news in your feed",
      description: "Students see forwarded messages and posts every day and can't easily tell what is true.",
      idea: "An AI that flags whether a message or headline looks reliable or suspicious, and explains why.",
      tags: ["Fact check", "Text AI"],
    },
  ],

  // The six stages shown in the notebook on /hackathon.
  journey: [
    {
      n: "01",
      title: "Register",
      text: "Your school nominates a coordinator, you form a team of 2–4 with a mentor teacher, and you sign up.",
      date: "10 Oct – 2 Nov 2026",
    },
    {
      n: "02",
      title: "Idea Round",
      text: "Submit the problem, your solution idea, who it helps and a 2-minute video. Paper-based work is accepted where connectivity is low.",
      date: "3 – 20 Nov 2026",
    },
    {
      n: "03",
      title: "Bootcamp",
      text: "The top ~600 teams join seven online evenings of design thinking, no-code, coding, AI tools, hardware and pitching.",
      date: "1 – 7 Dec 2026",
    },
    {
      n: "04",
      title: "Prototype",
      text: "Build it. Each team gets a mentor check-in, then submits a working prototype, a 3-minute demo video and a one-page write-up.",
      date: "8 – 31 Dec 2026",
    },
    {
      n: "05",
      title: "Finalists",
      text: "A jury of 8–10 experts scores every prototype, and the top 30 teams are announced.",
      date: "5 Jan 2027",
    },
    {
      n: "06",
      title: "Grand Finale",
      text: "30 teams pitch in New Delhi on National Youth Day, the top 3 present on the main stage, and the awards are announced.",
      date: "12 Jan 2027",
    },
  ],

  // Round 1 — idea submission
  round1: {
    window: "3 – 20 November 2026",
    submit: ["The problem", "Your solution idea", "Who it helps", "A 2-minute video"],
    lowConnectivity: "Schools with low connectivity may send photos of paper-based work instead of a video.",
    review: "Two reviewers score every entry between 21 and 28 November; a third breaks any tie.",
    results: "Top ~600 teams announced on 30 November 2026.",
    certificate: "Every team that submits receives a certificate.",
  },

  // Online bootcamp
  bootcamp: {
    window: "1 – 7 December 2026",
    time: "5:00 – 6:30 PM",
    mode: "Online",
    note: "Every session is recorded and shared with all registered students.",
    days: [
      { n: "Day 1", date: "1 Dec", title: "Kick-off", line: "Design thinking and understanding your user." },
      { n: "Day 2", date: "2 Dec", title: "No-code building", line: "App builders, Scratch and websites." },
      { n: "Day 3", date: "3 Dec", title: "Coding for prototypes", line: "Python and web basics." },
      { n: "Day 4", date: "4 Dec", title: "AI tools", line: "Using AI tools responsibly." },
      { n: "Day 5", date: "5 Dec", title: "Hardware & IoT", line: "Building with Atal Tinkering Lab kits." },
      { n: "Day 6", date: "6 Dec", title: "Mentor breakouts", line: "15 minutes one-on-one with your team's mentor." },
      { n: "Day 7", date: "7 Dec", title: "Pitching", line: "Making a clear 3-minute demo video." },
    ],
  },

  // Round 2 — prototype
  round2: {
    window: "8 – 31 December 2026",
    mentor: "Every bootcamp team gets one mentor check-in call.",
    submit: ["A working prototype", "A 3-minute demo video", "A one-page write-up"],
    deadline: "31 December 2026",
    jury: "8–10 experts score entries between 1 and 4 January 2027.",
    results: "Top 30 finalist teams announced on 5 January 2027.",
  },

  // Official scoring. Percentages are fixed — do not alter them.
  judging: {
    headline: "One open category. One scoring sheet.",
    note: "All teams are ranked together. A judge connected to any team steps aside from scoring it.",
    rounds: ["Round 1", "Round 2", "Finale"],
    // `line` and `asks` are plain-English glosses of the criterion name, written
    // for the page — the percentages are the official part and must not change.
    criteria: [
      {
        title: "Problem & Impact",
        weights: ["35%", "20%", "20%"],
        line: "The problem you chose, and who it helps.",
        asks: ["Real problem", "Who it helps", "Impact"],
      },
      {
        title: "Innovation",
        weights: ["30%", "20%", "20%"],
        line: "What is new about the way you solved it.",
        asks: ["Original idea", "Creative use of tech"],
      },
      {
        title: "Feasibility & Execution",
        weights: ["15%", "35%", "25%"],
        line: "Whether it can actually be built and run.",
        asks: ["Working prototype", "Practical", "Can scale"],
      },
      {
        title: "Presentation",
        weights: ["20%", "15%", "20%"],
        line: "How clearly your team tells the story.",
        asks: ["Clear demo", "Good structure"],
      },
      {
        title: "Q&A & Teamwork",
        weights: ["—", "10%", "15%"],
        line: "How the team answers questions and works together.",
        asks: ["Answers well", "Everyone contributes"],
      },
    ],
  },

  prizes: {
    // The podium amounts below were raised by the organising team on
    // 10 Oct 2026 and now supersede the execution plan's §14 figures.
    // Everything else here is still from the plan: the finalist medal and
    // certificate (§14) and the mentorship/incubation offer made to the top 5
    // teams (§15). Do not add perks the plan does not list.
    //
    // The advertised total comes from prizePool() below — it is derived from
    // these lists, so changing an amount here updates every page that shows it.
    includes: ["Cash prize", "Medal and certificate", "Mentorship or incubation offer"],
    podium: [
      { place: "1st prize",  value: "₹1,00,000" },
      { place: "2nd prize",  value: "₹50,000" },
      { place: "3rd prize",  value: "₹30,000" },
    ],
    specialValue: "₹10,000",
    special: [
      "Best Rural Impact",
      "Best Girls' Team",
      "Best Government-School Team",
      "Best Use of AI",
    ],
  },

  finale: {
    date: "12 January 2027",
    occasion: "National Youth Day",
    // Venue is NOT locked yet — publish the city only.
    city: "New Delhi",
    venue: TBD,
    teams: "30 finalist teams",
    people: "~100 students and 30 mentor teachers",
    schedule: [
      { time: "8:30 – 9:30 AM",   title: "Registration, breakfast and demo tables" },
      { time: "9:30 – 10:15 AM",  title: "Opening, National Youth Day message and chief guest" },
      { time: "10:15 AM – 1:15 PM", title: "Pitching across 3 jury panels — 5-minute pitch, 5-minute Q&A" },
      { time: "1:15 – 2:15 PM",   title: "Lunch and open demo exhibition" },
      { time: "2:15 – 3:30 PM",   title: "Top 3 teams pitch on the main stage" },
      { time: "3:30 – 4:15 PM",   title: "Guest talk while the jury decides" },
      { time: "4:15 – 5:00 PM",   title: "Awards, closing and group photo" },
    ],
  },

  // What happens after the finale
  afterwards: [
    { title: "Results and highlights", line: "Published within 3 days of the finale." },
    { title: "E-certificates", line: "Sent within 7 days to every participant and coordinator." },
    { title: "Mentorship or incubation", line: "Offered to the top 5 teams." },
    { title: "Impact report", line: "Published by 31 January 2027." },
  ],

  // One teacher per school runs YUVASETU inside that school
  coordinator: {
    title: "School Coordinator",
    line: "Each participating school nominates one teacher as its School Coordinator.",
    does: [
      "Registers the school",
      "Helps students form teams",
      "Collects parental consent forms",
      "Coordinates participation through the rounds",
    ],
    gets: ["Certificate", "Coordinator kit", "Recognition at the finale"],
  },

  inclusion: {
    title: "Built for every school",
    line: "Government and private, urban and rural, high and low connectivity.",
    items: [
      "Offline idea submission using photos of paper worksheets",
      "Every bootcamp session recorded and shared",
      "Key material in Hindi and regional languages",
      "Bulk registration through the school coordinator",
      "Fee-waiver support planned for low-income students",
    ],
  },

  safety: {
    title: "Safety and privacy",
    line: "Every participant is a minor, so the programme is run accordingly.",
    items: [
      "Parental consent is required for every student",
      "Separate consent is required for photos and video",
      "A teacher or staff member is present at every session",
      "No private one-to-one contact between an adult mentor and a student",
      "At the finale, students stay with their mentor teacher",
      "Separate rooms for girls, with female chaperones",
      "Only the data the programme needs is collected, and it is never shared with sponsors",
    ],
  },

  // ─── Previous programmes (LegacySection) ───────────────────────────────────
  // Real events already run. Photo slots map to files in
  // src/assets/hackathon/legacy named `<slug>-<slot>` — see the README there.
  previousPrograms: [
    {
      slug: "vidyasetu",
      n: "01",
      year: "Programme",
      name: "AI Vidya Setu 1.0",
      subtitle: "Code for New Bharat",
      blurb:
        "A national school hackathon where students from Classes 8–12 explored AI and machine learning to turn real-world problems into practical solutions — building problem solving, innovation and teamwork along the way.",
      tags: ["National hackathon", "Classes 8–12", "AI × Innovation"],
      meta: [
        { k: "Presented by", v: "IHFC (TIH of IIT Delhi) & Kendriya Vidyalaya Sangathan" },
        { k: "Format", v: "Foundation learning, regional and zonal stages, national round" },
        { k: "Aligned with", v: "NEP 2020 · Viksit Bharat 2047" },
      ],
      photos: [
        { slot: 1, alt: "Students at AI Vidya Setu 1.0" },
        { slot: 2, alt: "A team working on their project at AI Vidya Setu 1.0" },
        { slot: 3, alt: "Students presenting at AI Vidya Setu 1.0" },
      ],
    },
    {
      slug: "eduthon",
      n: "02",
      year: "22–24 December 2025",
      name: "AI Eduthon 2025",
      subtitle: "Techfest, IIT Bombay × CoGrad",
      blurb:
        "A 24-hour on-ground AI hackathon focused on building meaningful solutions for rural education — from multilingual learning to offline AI and teacher support.",
      tags: ["IIT Bombay", "24-hour hackathon", "AI × Education", "Rural India"],
      meta: [
        { k: "Venue", v: "IIT Bombay" },
        { k: "Problem areas", v: "Multilingual content, personalised lessons, teacher evaluation, offline AI for rural students" },
        { k: "Mentorship", v: "CoGrad and Medha AI experts" },
        { k: "Prize pool", v: "₹1.75 L total · ₹1.25 L grand champion" },
      ],
      photos: [
        { slot: 1, alt: "AI Eduthon 2025 at IIT Bombay" },
        { slot: 2, alt: "Teams building through the night at AI Eduthon 2025" },
        { slot: 3, alt: "Mentors reviewing a team's work at AI Eduthon 2025" },
        { slot: 4, alt: "Winning team at AI Eduthon 2025" },
      ],
    },
    {
      slug: "sankalp",
      n: "03",
      year: "Jan – Mar 2026",
      name: "SANKALP 2026 AI Hackathon",
      subtitle: "MNNIT Allahabad × CoGrad",
      blurb:
        "Building AI Solutions for Real-World India — an AI hackathon run during SANKALP 2026 at MNNIT Allahabad, where university teams took a problem from education, skilling or public decision-making all the way to a working prototype and a live technical defence.",
      tags: ["MNNIT Allahabad", "UG & PG teams", "AI × Real-world India"],
      meta: [
        { k: "Hosted at", v: "MNNIT Allahabad, Prayagraj" },
        { k: "Organised by", v: "Design Innovation Centre, MNNIT, with CoGrad" },
        { k: "Technology partner", v: "Medha AI" },
        { k: "Tracks", v: "Concept-to-visualisation intelligence, career guidance and upskilling, local leadership and public trust" },
        { k: "Grand Finale", v: "24–26 March 2026" },
        { k: "Prize pool", v: "₹1,00,000 total · ₹50,000 first prize" },
      ],
      photos: [
        { slot: 1, alt: "SANKALP 2026 AI Hackathon at MNNIT Allahabad" },
        { slot: 2, alt: "Teams building at the SANKALP 2026 AI Hackathon" },
        { slot: 3, alt: "Finale demonstrations at the SANKALP 2026 AI Hackathon" },
      ],
    },
  ],

  nextChapter: {
    n: "04",
    label: "The next chapter",
    name: "YUVASETU 2026",
    blurb:
      "A national school AI hackathon for the next generation of builders. This edition has been cancelled and every registration fee is being refunded.",
  },

  // Illustrative only — NOT official problem statements.
  projects: [
    {
      id: "resources",
      track: "Environment & Cleanliness",
      title: "School resource monitor",
      problem: "Lights, fans and taps are left running in empty classrooms.",
      approach: "Sensor data plus a simple model that learns normal usage and flags waste.",
      prototype: "A dashboard showing each room's usage, with alerts for the caretaker.",
      impact: "Lower electricity and water bills for the school.",
    },
    {
      id: "accessibility",
      track: "Education",
      title: "Live caption assistant",
      problem: "Classmates with hearing loss miss what is said in class.",
      approach: "Speech-to-text that captions the teacher live, with key words highlighted.",
      prototype: "A tablet app showing live captions and saving class notes.",
      impact: "Every student can follow the lesson in real time.",
    },
    {
      id: "air",
      track: "Health",
      title: "Air & water watch",
      problem: "No one knows when local air or water quality turns unsafe.",
      approach: "Low-cost sensors plus a model that predicts bad-air days.",
      prototype: "A neighbourhood map with daily readings and early warnings.",
      impact: "Families can plan ahead on high-pollution days.",
    },
    {
      id: "crop",
      track: "Agriculture & Rural Life",
      title: "Crop trouble spotter",
      problem: "Farmers notice crop disease only after it has spread.",
      approach: "A phone camera model trained to recognise early leaf symptoms.",
      prototype: "A photo-in, advice-out app that works offline.",
      impact: "Problems caught early, and less of the harvest lost.",
    },
    {
      id: "community",
      track: "Safety & Smart Communities",
      title: "Community fix-it platform",
      problem: "Broken streetlights and potholes go unreported for months.",
      approach: "Photo classification that sorts and routes complaints automatically.",
      prototype: "A reporting app with a public map of open and fixed issues.",
      impact: "Faster repairs and a more responsive neighbourhood.",
    },
  ],

  timeline: [
    { date: "2 October 2026",        title: "Launch",             text: "YUVASETU is announced on Gandhi Jayanti.", key: "launch" },
    { date: "10 Oct – 2 Nov 2026",   title: "Registration",       text: "Schools and students sign up online.",     key: "open" },
    { date: "3 – 20 November 2026",  title: "Round 1 — Idea",     text: "Teams submit their problem and idea." },
    { date: "30 November 2026",      title: "Round 1 results",    text: "Top ~600 teams move to the bootcamp." },
    { date: "1 – 7 December 2026",   title: "Bootcamp",           text: "Seven online evenings of building skills.", mode: "Online" },
    { date: "8 – 31 December 2026",  title: "Round 2 — Prototype", text: "Teams build and submit a working prototype." },
    { date: "5 January 2027",        title: "Top 30 announced",   text: "Finalist teams are named." },
    { date: "12 January 2027",       title: "Grand Finale",       text: "Finalists pitch in New Delhi on National Youth Day.", mode: "New Delhi" },
  ],

  audiences: [
    {
      id: "students",
      label: "Students",
      items: [
        { q: "What will I build?", a: "A technology solution to a real problem you have seen yourself — in your school, home, village or city. Pick one of the six challenge tracks." },
        { q: "How does it work?", a: "Register with your team, submit your idea in Round 1, join the bootcamp if you are selected, build a prototype in Round 2, and the top 30 teams pitch at the Grand Finale in New Delhi." },
        { q: "Do I need coding experience?", a: "No. The bootcamp covers no-code app builders, Scratch and websites as well as Python and web basics, so teams can build at the level they are comfortable with." },
      ],
    },
    {
      id: "parents",
      label: "Parents",
      items: [
        { q: "Who is eligible?", a: "Students of Classes 8 to 12 from any school in India — government or private, and any recognised board." },
        { q: "What does it cost?", a: "₹399 for a solo entry, and ₹299 per member when you register as a team." },
        { q: "Is my child safe?", a: "Parental consent is required for every student, and separately for photos and video. A teacher is present at every session, there is no private one-to-one adult contact, and at the finale students stay with their mentor teacher, with separate rooms and female chaperones for girls." },
        { q: "When does it happen?", a: "Registration runs from 10 October to 2 November 2026, the rounds run through November and December, and the Grand Finale is on 12 January 2027 in New Delhi." },
      ],
    },
    {
      id: "teachers",
      label: "Teachers",
      items: [
        { q: "How do teams work?", a: "A team is 2 to 4 students with 1 mentor teacher. All teams compete in one open category across Classes 8 to 12." },
        { q: "What does a School Coordinator do?", a: "Each school nominates one teacher as its School Coordinator, who registers the school, helps students form teams, collects consent forms and coordinates participation. Coordinators receive a certificate, a coordinator kit and recognition at the finale." },
        { q: "What if our school has poor connectivity?", a: "Teams may submit photos of paper-based work instead of a video in Round 1, every bootcamp session is recorded, key material is shared in Hindi and regional languages, and coordinators can register students in bulk." },
        { q: "What support do teams get?", a: "Selected teams get seven bootcamp evenings covering design thinking, no-code tools, coding, AI tools, hardware and pitching, plus a mentor check-in during the prototype round." },
      ],
    },
  ],

  faqs: [
    { q: "Who can participate?", a: "Students of Classes 8 to 12 from any school in India — government or private, across CBSE, ICSE, State and other recognised boards." },
    { q: "How big is a team?", a: "2 to 4 students with 1 mentor teacher, judged together in one open category. You can also register on your own and form or join a team before the Idea Round." },
    { q: "What is the theme?", a: "Tech for a Self-Reliant Bharat. Find a real problem in your school, home, village or city, and build a technology solution for it." },
    { q: "What are the challenge tracks?", a: "Six: Education, Health, Environment & Cleanliness, Agriculture & Rural Life, Safety & Smart Communities, and Open Innovation." },
    { q: "What is the registration fee?", a: "₹399 for a solo entry, or ₹299 per member when you register as a team of 2–4. A mentor teacher is never charged." },
    { q: "When does registration open and close?", a: "Registration ran from 10 October 2026 and closed on 2 November 2026 at 11:59 PM." },
    { q: "How do I pay?", a: "By UPI, card or net banking during registration." },
    { q: "What happens in Round 1?", a: "Between 3 and 20 November 2026 teams submit the problem, their solution idea, who it helps and a 2-minute video. Schools with low connectivity may send photos of paper-based work instead. The top ~600 teams are announced on 30 November." },
    { q: "What is the bootcamp?", a: "Seven online evenings from 1 to 7 December 2026, 5:00 to 6:30 PM, covering design thinking, no-code building, coding, AI tools, hardware and IoT, mentor breakouts and pitching. Every session is recorded and shared." },
    { q: "What do we submit in Round 2?", a: "A working prototype, a 3-minute demo video and a one-page write-up, by 31 December 2026. A jury of 8–10 experts scores entries and the top 30 teams are announced on 5 January 2027." },
    { q: "How are projects judged?", a: "On Problem & Impact, Innovation, Feasibility & Execution, Presentation, and — from Round 2 — Q&A & Teamwork. The weightage shifts from the idea in Round 1 to the build in Round 2 and the finale." },
    { q: "What can we win?", a: "₹1,00,000 for the winner, ₹50,000 for the first runner-up and ₹30,000 for the second runner-up, plus ₹10,000 special awards for Best Rural Impact, Best Girls' Team, Best Government-School Team and Best Use of AI. Every finalist receives a medal and certificate." },
    { q: "Where is the Grand Finale?", a: "In New Delhi on 12 January 2027, National Youth Day. The venue will be announced closer to the date." },
    { q: "Do I need coding experience?", a: "No. The bootcamp starts with no-code tools and design thinking before moving to Python and web basics, so teams can build at their own level." },
  ],

  contact: { email: "connect@cograd.in", phone: "+91 92202 53001" },
  socialLinks: [],

  legal: [
    { label: "Privacy Policy",     href: "/privacy-policy" },
    { label: "Terms & Conditions", href: "/terms-conditions" },
    { label: "Refund Policy",      href: null }, // not published yet
    { label: "Code of Conduct",    href: null }, // not published yet
  ],

  // Registration form options
  form: {
    states: [
      "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar",
      "Chandigarh", "Chhattisgarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Goa",
      "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", "Karnataka",
      "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
      "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan", "Sikkim",
      "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
    ],
    boards: ["CBSE", "ICSE", "State Board", "Other"],
    experience: ["No experience yet", "Beginner", "Some projects", "Confident"],
    sources: ["School / Teacher", "Friend", "Instagram", "WhatsApp", "YouTube", "LinkedIn", "Other"],
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ms = (iso) => (iso ? new Date(iso).getTime() : null);

/**
 * "upcoming" | "open" | "closed". `?reg=` previews another state — but once the
 * window has really ended nothing reopens it, so a shared `?reg=open` link
 * cannot put the form back in front of anyone.
 */
export function registrationState(now = Date.now()) {
  if (hackathon.cancellation?.active) return "closed";
  const opens = ms(hackathon.registration.opensAt);
  const closes = ms(hackathon.registration.closesAt);
  if (closes && now > closes) return "closed";
  if (typeof window !== "undefined") {
    const q = new URLSearchParams(window.location.search).get("reg");
    if (q === "open" || q === "upcoming" || q === "closed") return q;
  }
  if (opens && now < opens) return "upcoming";
  return "open";
}

/** True while the cancellation notice is in force. */
export const isCancelled = () => Boolean(hackathon.cancellation?.active);

export const isTBD = (v) => v === TBD || v === SOON || v == null;

/** "1996" → "₹1,996". One place, so every total on the page reads the same. */
export const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

/** Per-student fee for a registration mode ("individual" or "team"). */
export const feeFor = (mode) =>
  mode === "team" ? hackathon.registration.fee.team : hackathon.registration.fee.individual;

/** What `students` paying students cost in `mode`, in rupees. */
export const registrationTotal = (mode, students) =>
  Math.max(0, Math.round(students)) * feeFor(mode);

/**
 * Total advertised prize money, in rupees — the three podium prizes plus one
 * special award per category. Derived rather than written down so the figure
 * can never contradict the lists above.
 */
export function prizePool() {
  const amount = (v) => Number(String(v).replace(/[^0-9]/g, "")) || 0;
  const { podium, special, specialValue } = hackathon.prizes;
  return (
    podium.reduce((sum, p) => sum + amount(p.value), 0) +
    amount(specialValue) * special.length
  );
}

/**
 * The same figure written out in full, Indian grouping — "₹2,20,000". Short
 * forms like "₹2.2 L" round the number and read like an estimate, so the whole
 * amount is printed instead.
 */
export function prizePoolLabel() {
  return `₹${prizePool().toLocaleString("en-IN")}`;
}
